/**
 * ⚠️ MATRIX PARITY:
 * Any change to this Drive Mode capability matrix MUST be mirrored in
 * `backend/engine/drive_mode/__init__.py` and covered by
 * `backend/tests/test_drive_mode_matrix_parity.py`.
 * See /app/memory/DRIVE_MODE_STATE_ARCHITECTURE.md §13.
 */

/**
 * driveMode — frontend mirror of `engine/drive_mode` rule layer.
 *
 * Stage-2 P1 follow-up — Drive Mode P0 (Feb 18, 2026).
 *
 * Doctrine:
 *   • Pure function. No state. No side effects.
 *   • Mirrors the backend rule table EXACTLY. A doctrinal change
 *     MUST be reflected here AND in backend/engine/drive_mode/
 *     in the same commit (a contract test guards this).
 *   • `unknown` collapses to the same restricted matrix as
 *     `moving` — safe baseline.
 *   • `can_override` is always true. Drive Mode advises; the
 *     instructor decides.
 */

export const MOVING_STATES = Object.freeze(["unknown", "parked", "moving"]);

export const CAPABILITY_KEYS = Object.freeze([
  "read_only_glance",
  "open_in_maps_link",
  "interactive_actions",
  "typing",
  "scrolling_prompts",
  "popups",
  "animations",
  "decision_heavy_actions",
  "notes_and_reflections",
  "progression_actions",
]);

const CAPABILITIES_BY_STATE = {
  moving: {
    read_only_glance:       true,
    open_in_maps_link:      true,
    interactive_actions:    false,
    typing:                 false,
    scrolling_prompts:      false,
    popups:                 false,
    animations:             false,
    decision_heavy_actions: false,
    notes_and_reflections:  false,
    progression_actions:    false,
  },
  parked: {
    read_only_glance:       true,
    open_in_maps_link:      true,
    interactive_actions:    true,
    typing:                 true,
    scrolling_prompts:      true,
    popups:                 true,
    animations:             true,
    decision_heavy_actions: true,
    notes_and_reflections:  true,
    progression_actions:    true,
  },
  // 'unknown' === 'moving' by design (safe baseline).
  unknown: {
    read_only_glance:       true,
    open_in_maps_link:      true,
    interactive_actions:    false,
    typing:                 false,
    scrolling_prompts:      false,
    popups:                 false,
    animations:             false,
    decision_heavy_actions: false,
    notes_and_reflections:  false,
    progression_actions:    false,
  },
};

const LABEL = {
  parked:  "Parked",
  moving:  "Moving",
  unknown: "Unknown — treating as moving",
};

const NOTE = {
  parked:
    "Interaction allowed. Notes, reflections, and progression actions are safe to surface.",
  moving:
    "Read-only glance only. No typing, no decision-heavy actions. Instructor remains in control.",
  unknown:
    "Safe baseline. Surfaces stay glance-only until parked is confirmed.",
};

export function normaliseMovingState(raw) {
  if (typeof raw === "string" && raw in CAPABILITIES_BY_STATE) return raw;
  return "unknown";
}

export function driveModeCapabilities(movingState) {
  const state = normaliseMovingState(movingState);
  return {
    moving_state: state,
    capabilities: { ...CAPABILITIES_BY_STATE[state] },
    label:        LABEL[state],
    note:         NOTE[state],
    can_override: true,
    doctrine: {
      read_only:    true,
      auto_actions: false,
      system_role:  "suggest_only",
    },
  };
}

export function isCapabilityAllowed(movingState, capability) {
  const caps = CAPABILITIES_BY_STATE[normaliseMovingState(movingState)] || {};
  return Boolean(caps[capability]);
}
